// searchProjection — the ONE pure Library → SearchResult[] projection behind
// the Search surface. Folds the SAME `Library` the omnibar's `libraryCommandItems`
// (app/searchCorpus.ts) folds, plus the conversation summaries the rail already
// holds — one corpus, a richer target shape, so the two surfaces cannot drift.
//
// Pure: a data→data map. `nowMs` is the ONLY ambient input, passed in as a plain
// number by the impure shell (the host's sanctioned `useNow(30_000)` seam), so
// freshness is computed here without this module ever reading the clock — "now"
// stays an input, never a `Date.now()` call in the projection. This is the exact
// split HeroHost uses for its "senast sammanfattad" recency label. Presentation
// stays out: freshness is emitted as a structured `Recency` sum, localized by
// SearchSurface, never a pre-baked display string.

import type {
    Library,
    LibraryItem,
    SourceBibliography,
    SourceItem,
} from "../../app/ports.js";
import type { RecentThread } from "../WorkspaceLanding/WorkspaceLanding.js";
import { citedName } from "./authorName.js";
import type {
    FreshnessBucket,
    Recency,
    RowFacts,
    SearchResult,
    SearchResultKind,
} from "./searchModel.js";

const DAY_MS = 86_400_000;

// Local-calendar midnight for an epoch ms — the day boundary freshness buckets
// against (a resource edited at 23:00 and read at 01:00 is "yesterday", not
// "2 hours ago"). Uses the host's local timezone, a stable ambient of the
// running shell, not a per-call impurity.
const startOfDay = (ms: number): number => {
    const d = new Date(ms);
    return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
};

// Bucket + structured relative-time from an ISO 8601 `lastEditedAt` against
// `nowMs`. An absent or unparseable timestamp degrades to the "earlier" bucket
// with `unknown` recency (absent-not-fabricated) — never a crash, never a
// made-up time. Pure.
const freshnessOf = (
    lastEditedAt: string | undefined,
    nowMs: number,
): { readonly bucket: FreshnessBucket; readonly recency: Recency } => {
    if (lastEditedAt === undefined)
        return { bucket: "earlier", recency: { type: "unknown" } };
    const t = Date.parse(lastEditedAt);
    if (Number.isNaN(t))
        return { bucket: "earlier", recency: { type: "unknown" } };

    const dayDiff = Math.round((startOfDay(nowMs) - startOfDay(t)) / DAY_MS);
    const elapsed = Math.max(0, nowMs - t);

    if (dayDiff <= 0) {
        if (elapsed < 60_000)
            return { bucket: "today", recency: { type: "now" } };
        if (elapsed < 3_600_000)
            return {
                bucket: "today",
                recency: { type: "minutes", n: Math.round(elapsed / 60_000) },
            };
        return {
            bucket: "today",
            recency: { type: "hours", n: Math.round(elapsed / 3_600_000) },
        };
    }
    if (dayDiff === 1)
        return { bucket: "yesterday", recency: { type: "yesterday" } };
    if (dayDiff < 7)
        return { bucket: "this-week", recency: { type: "days", n: dayDiff } };

    const then = new Date(t);
    return {
        bucket: "earlier",
        recency: { type: "date", day: then.getDate(), month: then.getMonth() },
    };
};

// A source/package title is a filename; its extension is the one honest format
// tag we can show ("report.pdf" → "PDF"). No extension ⇒ "" (no meta; the
// workspace name is never repeated on every row) — never a fabricated format.
const extensionTag = (title: string): string => {
    const dot = title.lastIndexOf(".");
    if (dot <= 0 || dot === title.length - 1) return "";
    const ext = title.slice(dot + 1);
    return ext.length > 0 && ext.length <= 5 ? ext.toUpperCase() : "";
};

// A library item's search kind (the wire's "export" is the Library's "package").
const kindOfItem = (item: LibraryItem): SearchResultKind => {
    switch (item.type) {
        case "source":
            return "source";
        case "material":
            return "material";
        case "export":
            return "package";
        case "file":
            return "file";
    }
};

// Who wrote the work + when, for a row's quiet line: "Linderson · 2025",
// "Bird & de Moor · 1997", "Meijer m.fl." (3+ authors, via the localized
// `etAl`); people by family name, organisations whole ("Natur & Kultur",
// and then joined by a comma). No authors and no year => undefined (the row
// falls back to its kind/format facts). Pure.
export const bylineOf = (
    bibliography: SourceBibliography | undefined,
    etAl: (first: string) => string,
): string | undefined => {
    if (bibliography === undefined) return undefined;
    const names = bibliography.authors.map(citedName);
    const [first = "", second = ""] = names;
    const joiner = names.some((n) => n.includes("&")) ? ", " : " & ";
    const authors =
        names.length < 3
            ? [first, second].filter((n) => n !== "").join(joiner)
            : etAl(first);
    const parts = [
        authors,
        bibliography.year === undefined ? "" : String(bibliography.year),
    ].filter((p) => p !== "");
    return parts.length === 0 ? undefined : parts.join(" · ");
};

// A source's friendly name: its own printed title, else enrichment's listing
// title, else the filename. Pure.
const sourceTitleOf = (source: SourceItem, untitled: string): string =>
    source.bibliography?.title ??
    source.displayTitle ??
    source.title ??
    untitled;

// What a material or package row says it is (sources and files say it with
// their byline and format).
const rowFactsOf = (item: LibraryItem): RowFacts | undefined => {
    switch (item.type) {
        case "material":
            return {
                type: "material",
                basedOn: item.basedOn ?? [],
                ...(item.template === undefined
                    ? {}
                    : { template: item.template }),
                ...(item.versionCount === undefined
                    ? {}
                    : { versions: item.versionCount }),
            };
        case "export":
            return {
                type: "package",
                format: item.format ?? { type: "unspecified" },
                ...(item.packagedFrom === undefined
                    ? {}
                    : { from: item.packagedFrom }),
            };
        case "source":
        case "file":
            return undefined;
    }
};

// The search facts every library row carries (absent stays absent): creation
// for `created:`, last touched for the Edited column, the "used in N" count, a
// source's full author names, and what a material or package row says it is.
const factsOf = (
    item: LibraryItem,
): Pick<
    SearchResult,
    "createdAt" | "editedAt" | "usedIn" | "authors" | "facts" | "aiAuthored"
> => {
    const facts = rowFactsOf(item);
    return {
        ...(item.createdAt === undefined ? {} : { createdAt: item.createdAt }),
        ...(item.lastEditedAt === undefined
            ? {}
            : { editedAt: item.lastEditedAt }),
        ...(item.usedIn === undefined ? {} : { usedIn: item.usedIn }),
        ...(item.type === "source" && item.bibliography !== undefined
            ? { authors: item.bibliography.authors }
            : {}),
        ...(facts === undefined ? {} : { facts }),
        ...(item.type === "material" && item.aiAuthored !== undefined
            ? { aiAuthored: true }
            : {}),
    };
};

// The ONE projection. A null library (not yet loaded) ⇒ no rows. Conversations
// carry a precomputed `when` label but no bucketable timestamp (RecentThread is
// {id,title,when}), so they land in the "earlier" bucket with `when` passed
// through as `raw` recency — an honest limitation of the current summary shape,
// not a fabricated bucket (see the stub note in SearchSurface.tsx's header).
export const libraryToSearchResults = (
    library: Library | null,
    conversations: readonly RecentThread[],
    nowMs: number,
    // F63 — localized neutral title for a titleless (no-first-message) thread.
    conversationFallbackTitle: string,
    untitled: string,
    // Localized "<first author> et al." for a 3+-author byline.
    etAl: (first: string) => string,
): readonly SearchResult[] => {
    if (library === null) return [];
    const location = library.workspaceName;

    const sources: readonly SearchResult[] = library.sources.map((s) => {
        const fresh = freshnessOf(s.lastEditedAt, nowMs);
        return {
            kind: "source",
            id: s.id,
            title: sourceTitleOf(s, untitled),
            location,
            recency: fresh.recency,
            freshnessBucket: fresh.bucket,
            meta: extensionTag(s.title ?? ""),
            byline: bylineOf(s.bibliography, etAl),
            ...factsOf(s),
            // Only a source still being read, or failed, says so (FR-3.3).
            ...(s.readStatus === undefined ||
            s.readStatus.type === "source_read_ready"
                ? {}
                : { readStatus: s.readStatus }),
        };
    });

    const materials: readonly SearchResult[] = library.materials.map((m) => {
        const fresh = freshnessOf(m.lastEditedAt, nowMs);
        return {
            kind: "material",
            id: m.id,
            title: m.title ?? untitled,
            location,
            recency: fresh.recency,
            freshnessBucket: fresh.bucket,
            meta: "",
            ...factsOf(m),
        };
    });

    const packages: readonly SearchResult[] = library.exports.map((e) => {
        const fresh = freshnessOf(e.lastEditedAt, nowMs);
        return {
            kind: "package",
            id: e.id,
            title: e.title ?? untitled,
            location,
            recency: fresh.recency,
            freshnessBucket: fresh.bucket,
            meta: extensionTag(e.title ?? ""),
            ...factsOf(e),
        };
    });

    // A file's title is its filename; the extension is the honest format tag
    // (the /state wire carries no media type or size — the detail reads those).
    const files: readonly SearchResult[] = library.files.map((f) => {
        const fresh = freshnessOf(f.lastEditedAt, nowMs);
        return {
            kind: "file",
            id: f.id,
            title: f.title ?? untitled,
            location,
            recency: fresh.recency,
            freshnessBucket: fresh.bucket,
            meta: extensionTag(f.title ?? ""),
            ...factsOf(f),
        };
    });

    // F86 — archived resources (every kind) carry `archived: true`; the
    // Library's scope logic keeps them out of every scope but "archived".
    const archived: readonly SearchResult[] = library.archived.map((item) => {
        const fresh = freshnessOf(item.lastEditedAt, nowMs);
        const kind = kindOfItem(item);
        return {
            kind,
            id: item.id,
            title:
                item.type === "source"
                    ? sourceTitleOf(item, untitled)
                    : (item.title ?? untitled),
            location,
            recency: fresh.recency,
            freshnessBucket: fresh.bucket,
            meta: kind === "material" ? "" : extensionTag(item.title ?? ""),
            byline:
                item.type === "source"
                    ? bylineOf(item.bibliography, etAl)
                    : undefined,
            archived: true,
            ...factsOf(item),
        };
    });

    const convos: readonly SearchResult[] = conversations.map((c) => ({
        kind: "conversation",
        id: c.id,
        title: c.title.trim() === "" ? conversationFallbackTitle : c.title,
        location,
        recency: { type: "raw", text: c.when },
        freshnessBucket: "earlier",
        meta: "",
    }));

    return [
        ...sources,
        ...materials,
        ...packages,
        ...files,
        ...archived,
        ...convos,
    ];
};
